import React, { useState } from "react";
import SyntaxiDemo from './components/SyntaxiDemo';
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Users, FileText, Calendar, BarChart3, Plus, Search } from "lucide-react";

const CRMDemo = () => {
  const [clients] = useState([
    { id: 1, name: "Γεώργιος Παπαδόπουλος", email: "g.papadopoulos@example.com", phone: "+30 210 1234567", cases: 3 },
    { id: 2, name: "Μαρία Κωνσταντίνου", email: "m.konstantinou@example.com", phone: "+30 210 2345678", cases: 1 },
    { id: 3, name: "Δημήτρης Νικολάου", email: "d.nikolaou@example.com", phone: "+30 210 3456789", cases: 2 }
  ]);

  const [plan, setPlan] = useState('starter');
  const [cases] = useState([
    { id: 1, clientName: "Γεώργιος Παπαδόπουλος", caseNumber: "2025-001", type: "Αστικό", status: "Ενεργή" },
    { id: 2, clientName: "Μαρία Κωνσταντίνου", caseNumber: "2025-002", type: "Εργατικό", status: "Ενεργή" },
    { id: 3, clientName: "Δημήτρης Νικολάου", caseNumber: "2025-003", type: "Ποινικό", status: "Εκκρεμής" }
  ]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_ailegalpro/artifacts/wofbxh36_Untitled%20design%20%2821%29.png" 
                alt="ΝΟΜΟΚΡΑΤΙΑ Logo" 
                className="h-12 w-auto"
              />
              <span className="ml-4 text-2xl font-bold text-gray-900">CRM Demo</span>
            </div>
            <Button onClick={() => window.location.href = "/"} variant="outline">
              Επιστροφή στην Αρχική
            </Button>
          </div>
        </div>
      </header>

      {/* Plan Selector */}
      <div className="bg-white border-b py-3 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <span className="text-sm text-gray-600 font-medium">🎯 Demo — Επιλέξτε πλάνο:</span>
            <div className="flex gap-2">
              <button onClick={() => setPlan('starter')} className={plan==='starter' ? 'px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white border border-blue-600' : 'px-4 py-2 rounded-lg text-sm font-semibold bg-white text-gray-700 border border-gray-300'}>Starter €39/μήνα</button>
              <button onClick={() => setPlan('pro')} className={plan==='pro' ? 'px-4 py-2 rounded-lg text-sm font-semibold bg-green-600 text-white border border-green-600' : 'px-4 py-2 rounded-lg text-sm font-semibold bg-white text-gray-700 border border-gray-300'}>Pro €199/μήνα</button>
            </div>
            {plan==='starter' && <span className="text-xs text-gray-500">Αναζήτηση + Σύνταξη</span>}
            {plan==='pro' && <span className="text-xs text-green-600 font-medium">✓ Πλήρης πρόσβαση</span>}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Dashboard Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Ενεργοί Πελάτες</CardTitle>
              <Users className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{clients.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Ενεργές Υποθέσεις</CardTitle>
              <FileText className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{cases.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Ραντεβού Μήνα</CardTitle>
              <Calendar className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">12</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Έγγραφα</CardTitle>
              <BarChart3 className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">47</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="clients" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="clients" disabled={plan==='starter'} className={plan==='starter' ? 'opacity-50 cursor-not-allowed' : ''}>
              {plan==='starter' ? '🔒 ' : ''}Πελάτες
            </TabsTrigger>
            <TabsTrigger value="cases" disabled={plan==='starter'} className={plan==='starter' ? 'opacity-50 cursor-not-allowed' : ''}>
              {plan==='starter' ? '🔒 ' : ''}Υποθέσεις
            </TabsTrigger>
            <TabsTrigger value="calendar" disabled={plan==='starter'} className={plan==='starter' ? 'opacity-50 cursor-not-allowed' : ''}>
              {plan==='starter' ? '🔒 ' : ''}Ημερολόγιο
            </TabsTrigger>
            <TabsTrigger value="syntaxi">✍️ Σύνταξη</TabsTrigger>
          </TabsList>
          {plan==='starter' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
              <span className="text-sm text-amber-800">🔒 Πελάτες, Υποθέσεις και Ημερολόγιο είναι διαθέσιμα στο <strong>Pro Plan €199/μήνα</strong></span>
              <button onClick={() => setPlan('pro')} className="bg-green-600 text-white px-4 py-1.5 rounded-lg text-sm font-semibold hover:bg-green-700 whitespace-nowrap">Δείτε το Pro →</button>
            </div>
          )}

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                  <CardTitle>Διαχείριση Πελατών</CardTitle>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Input placeholder="Αναζήτηση..." className="flex-1 sm:w-48" />
                    <Button className="whitespace-nowrap">
                      <Plus className="h-4 w-4 mr-2" />
                      Νέος Πελάτης
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {clients.map(client => (
                    <div key={client.id} className="flex items-center justify-between p-3 sm:p-4 border rounded-lg hover:bg-gray-50 gap-2">
                      <div className="flex items-center space-x-4">
                        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 font-bold text-lg">
                            {client.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{client.name}</h3>
                          <p className="text-xs sm:text-sm text-gray-600 truncate max-w-[140px] sm:max-w-none">{client.email}</p>
                          <p className="text-xs sm:text-sm text-gray-500">{client.phone}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">{client.cases} Υποθέσεις</p>
                        <Button variant="outline" size="sm" className="mt-2">
                          Προβολή
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cases Tab */}
          <TabsContent value="cases" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Διαχείριση Υποθέσεων</CardTitle>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Νέα Υπόθεση
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-semibold text-gray-700">Αριθμός</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Πελάτης</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Τύπος</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Κατάσταση</th>
                        <th className="text-left p-3 font-semibold text-gray-700">Ενέργειες</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cases.map(caseItem => (
                        <tr key={caseItem.id} className="border-b hover:bg-gray-50">
                          <td className="p-3">{caseItem.caseNumber}</td>
                          <td className="p-3">{caseItem.clientName}</td>
                          <td className="p-3">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                              {caseItem.type}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-sm ${
                              caseItem.status === "Ενεργή" 
                                ? "bg-green-100 text-green-800" 
                                : "bg-yellow-100 text-yellow-800"
                            }`}>
                              {caseItem.status}
                            </span>
                          </td>
                          <td className="p-3">
                            <Button variant="outline" size="sm">
                              Προβολή
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Ημερολόγιο Ραντεβού</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">Το ημερολόγιο είναι διαθέσιμο στην πλήρη έκδοση</p>
                  <Button onClick={() => window.location.href = "/"}>
                    Εγγραφείτε Τώρα
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="syntaxi" className="space-y-4">
            <SyntaxiDemo />
          </TabsContent>
        </Tabs>

        {/* CTA Section */}
        <Card className="mt-8 bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200">
          <CardContent className="text-center py-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Έτοιμοι για την πλήρη έκδοση;
            </h3>
            <p className="text-gray-700 mb-6">
              Ξεκλειδώστε όλες τις δυνατότητες με δωρεάν δοκιμή 7 ημερών!
            </p>
            <div className="flex justify-center gap-4">
              <Button 
                onClick={() => window.location.href = "/"} 
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                🎁 Δωρεάν Δοκιμή 7 Ημερών
              </Button>
              <Button 
                onClick={() => window.location.href = "/"} 
                variant="outline"
              >
                Μάθετε Περισσότερα
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CRMDemo;